// AI property search.
//
// Primary path: POST the query to the backend, which calls Claude (Haiku 4.5)
// to convert NL → structured filters. Backend returns within ~300ms typically.
//
// Fallback path: if the backend is down / key missing / network drops, we run
// a local regex parser so search still works (just less smart). Same schema
// either way, so applyAiFilters doesn't care which one produced it.
//
// Schema (filters object):
//   exactBeds / minBeds / maxBeds
//   exactBaths / minBaths / maxBaths
//   exactCarSpaces / minCarSpaces
//   minPrice / maxPrice
//   suburb, category, features[], listingType, methodOfSale, keywords

import api from "../../../api";

const PROPERTY_TYPE_MAP = [
    { keys: ["house", "houses"], value: "house" },
    { keys: ["unit", "units", "apartment", "apartments", "flat", "flats"], value: "unit" },
    { keys: ["townhouse", "townhouses"], value: "townhouse" },
    { keys: ["villa", "villas"], value: "villa" },
    { keys: ["land", "block"], value: "land" },
    { keys: ["acreage", "rural", "farm"], value: "acreage" },
    { keys: ["commercial", "office", "retail", "warehouse"], value: "commercial" },
];

const STOP_WORDS = new Set([
    "a", "an", "the", "with", "and", "or", "in", "at", "on", "of", "for",
    "to", "any", "property", "properties", "home", "homes", "place", "places",
    "near", "around", "by", "looking", "want", "need", "show", "me", "find",
    "search", "please", "i", "im", "i'm",
]);

const parseMoney = (raw) => {
    if (!raw) return null;
    let s = String(raw).toLowerCase().replace(/[\s,$]/g, "");
    let multiplier = 1;
    if (s.endsWith("m")) {
        multiplier = 1_000_000;
        s = s.slice(0, -1);
    } else if (s.endsWith("k")) {
        multiplier = 1_000;
        s = s.slice(0, -1);
    }
    const n = parseFloat(s);
    if (Number.isNaN(n)) return null;
    return Math.round(n * multiplier);
};

const MONEY_TOKEN = "\\$?\\d+(?:[.,]\\d+)?\\s*[mk]?";

const WORD_NUMS = {
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
};

// Pick exact vs minimum semantics based on surrounding context.
// "2 bedroom" / "two bed" -> exact
// "2+ bedroom" / "at least 2 beds" / "minimum 3 baths" -> minimum
const parseCount = (working, kindRe) => {
    // Try minimum patterns first ("at least N", "N+", "min N", "minimum N", "from N", "more than N")
    const minPatterns = [
        new RegExp(`(?:at\\s+least|minimum|min|from|more\\s+than)\\s*(\\d+|one|two|three|four|five|six|seven|eight|nine|ten)\\s*(?:-)?\\s*${kindRe}`, "i"),
        new RegExp(`(\\d+|one|two|three|four|five|six|seven|eight|nine|ten)\\s*\\+\\s*(?:-)?\\s*${kindRe}`, "i"),
    ];
    for (const re of minPatterns) {
        const m = working.match(re);
        if (m) {
            const n = parseInt(m[1], 10) || WORD_NUMS[m[1].toLowerCase()];
            return { type: "min", value: n, match: m[0] };
        }
    }
    // Maximum patterns ("up to N", "no more than N", "max N")
    const maxRe = new RegExp(`(?:up\\s+to|no\\s+more\\s+than|maximum|max)\\s*(\\d+|one|two|three|four|five|six|seven|eight|nine|ten)\\s*(?:-)?\\s*${kindRe}`, "i");
    const maxM = working.match(maxRe);
    if (maxM) {
        const n = parseInt(maxM[1], 10) || WORD_NUMS[maxM[1].toLowerCase()];
        return { type: "max", value: n, match: maxM[0] };
    }
    // Bare number = exact
    const exactRe = new RegExp(`(\\d+|one|two|three|four|five|six|seven|eight|nine|ten)\\s*(?:-)?\\s*${kindRe}`, "i");
    const exactM = working.match(exactRe);
    if (exactM) {
        const n = parseInt(exactM[1], 10) || WORD_NUMS[exactM[1].toLowerCase()];
        return { type: "exact", value: n, match: exactM[0] };
    }
    return null;
};

/**
 * Local regex parser. Used as a fallback when the Claude endpoint is unavailable
 * and also for synchronous instant-feedback while the Claude call is in flight.
 */
export const parsePropertySearch = (query) => {
    const result = { filters: { features: [] }, keywords: "" };
    if (!query || typeof query !== "string") return result;

    let working = " " + query.toLowerCase() + " ";

    const maxRe = new RegExp(`(?:under|below|less than|max|up to|<=?)\\s*(${MONEY_TOKEN})`, "i");
    const maxMatch = working.match(maxRe);
    if (maxMatch) {
        const v = parseMoney(maxMatch[1]);
        if (v != null) result.filters.maxPrice = v;
        working = working.replace(maxMatch[0], " ");
    }

    const minRe = new RegExp(`(?:over|above|more than|min|from|>=?)\\s*(${MONEY_TOKEN})`, "i");
    const minMatch = working.match(minRe);
    if (minMatch) {
        const v = parseMoney(minMatch[1]);
        if (v != null) result.filters.minPrice = v;
        working = working.replace(minMatch[0], " ");
    }

    const rangeRe = new RegExp(`(?:between\\s*)?(${MONEY_TOKEN})\\s*(?:-|to|and)\\s*(${MONEY_TOKEN})`, "i");
    const rangeMatch = working.match(rangeRe);
    if (rangeMatch && (result.filters.minPrice == null || result.filters.maxPrice == null)) {
        const lo = parseMoney(rangeMatch[1]);
        const hi = parseMoney(rangeMatch[2]);
        const looksLikeMoney = /[\$km]/i.test(rangeMatch[0]) || (lo != null && lo >= 1000) || (hi != null && hi >= 1000);
        if (looksLikeMoney) {
            if (lo != null && result.filters.minPrice == null) result.filters.minPrice = lo;
            if (hi != null && result.filters.maxPrice == null) result.filters.maxPrice = hi;
            working = working.replace(rangeMatch[0], " ");
        }
    }

    const beds = parseCount(working, "(?:bed(?:s|room|rooms)?|br|bdr)\\b");
    if (beds) {
        if (beds.type === "exact") result.filters.exactBeds = beds.value;
        if (beds.type === "min") result.filters.minBeds = beds.value;
        if (beds.type === "max") result.filters.maxBeds = beds.value;
        working = working.replace(beds.match, " ");
    }

    const baths = parseCount(working, "(?:bath(?:s|room|rooms)?|ba)\\b");
    if (baths) {
        if (baths.type === "exact") result.filters.exactBaths = baths.value;
        if (baths.type === "min") result.filters.minBaths = baths.value;
        if (baths.type === "max") result.filters.maxBaths = baths.value;
        working = working.replace(baths.match, " ");
    }

    const cars = parseCount(working, "(?:car|garage|garages|parking|park|spaces?)\\b");
    if (cars) {
        if (cars.type === "exact") result.filters.exactCarSpaces = cars.value;
        if (cars.type === "min") result.filters.minCarSpaces = cars.value;
        working = working.replace(cars.match, " ");
    }

    for (const t of PROPERTY_TYPE_MAP) {
        for (const k of t.keys) {
            const re = new RegExp(`\\b${k}\\b`, "i");
            if (re.test(working)) {
                result.filters.category = t.value;
                working = working.replace(re, " ");
                break;
            }
        }
        if (result.filters.category) break;
    }

    const featureKeywords = [
        "pool", "garage", "balcony", "garden", "ensuite", "study",
        "aircon", "air conditioning", "fireplace", "view", "ocean", "waterfront",
        "renovated", "new", "modern", "heritage",
    ];
    featureKeywords.forEach((f) => {
        const re = new RegExp(`\\b${f}\\b`, "i");
        if (re.test(working)) {
            result.filters.features.push(f);
            working = working.replace(re, " ");
        }
    });

    // Sale state. Check "not sold" / "unsold" before bare "sold" so the negation wins.
    if (/\b(?:not\s+sold|unsold|exclude\s+sold|excluding\s+sold|available)\b/i.test(working)) {
        result.filters.excludeSold = true;
        working = working.replace(/\b(?:not\s+sold|unsold|exclude\s+sold|excluding\s+sold|available)\b/gi, " ");
    } else if (/\bsold\b/i.test(working)) {
        result.filters.status = "sold";
        working = working.replace(/\bsold\b/gi, " ");
    }
    if (/\boff[\s-]?market\b/i.test(working)) {
        result.filters.status = "off_market";
        working = working.replace(/\boff[\s-]?market\b/gi, " ");
    } else if (/\bon[\s-]?market\b/i.test(working)) {
        result.filters.status = "on_market";
        working = working.replace(/\bon[\s-]?market\b/gi, " ");
    }
    if (/\bwithdrawn\b/i.test(working)) {
        result.filters.status = "withdrawn";
        working = working.replace(/\bwithdrawn\b/gi, " ");
    }

    const suburbRe = /\bin\s+([a-z][a-z\s'-]*?)(?:\s+(?:under|over|with|and|or|near|for|$)|$)/i;
    const suburbMatch = query.match(suburbRe);
    if (suburbMatch) {
        const suburb = suburbMatch[1].trim().replace(/\s+/g, " ");
        if (suburb && suburb.length < 40) {
            result.filters.suburb = suburb;
            working = working.replace(new RegExp(`\\bin\\s+${suburb}\\b`, "i"), " ");
        }
    }

    const tokens = working
        .replace(/[^a-z0-9\s]/gi, " ")
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t && !STOP_WORDS.has(t.toLowerCase()));
    result.keywords = tokens.join(" ").trim();

    return result;
};

/**
 * Async parser: asks the Claude endpoint first, falls back to local regex.
 * Returns the same { filters, keywords } shape as parsePropertySearch so callers
 * are interchangeable.
 */
export const parsePropertySearchAI = async (query) => {
    const local = parsePropertySearch(query);
    if (!query || !query.trim()) return local;

    try {
        const res = await api.post("property-management/properties/ai-search/", { query });
        const data = res?.data;
        if (data && data.source === "claude" && data.filters && typeof data.filters === "object") {
            // Claude returned structured filters. Keep regex-derived keywords as a
            // residual — Claude's `keywords` field covers the same intent.
            return {
                filters: {
                    features: [],
                    ...data.filters,
                },
                keywords: data.filters.keywords || "",
            };
        }
    } catch (e) {
        // Network/auth/etc. — fall through to local result.
        if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn("AI search backend unavailable, using local parser:", e?.message);
        }
    }
    return local;
};

/**
 * Apply parsed filters to a list of already-fetched properties so the
 * UI updates immediately while we wait for the backend to refetch.
 */
export const applyAiFilters = (properties, parsed) => {
    if (!parsed) return properties;
    const f = parsed.filters || {};
    const kw = (parsed.keywords || "").toLowerCase().trim();

    const numeric = (v) => {
        if (typeof v === "number") return v;
        if (!v) return 0;
        const n = parseFloat(String(v).replace(/[^0-9.-]+/g, ""));
        return Number.isNaN(n) ? 0 : n;
    };

    return properties.filter((p) => {
        const beds = numeric(p.beds);
        if (f.exactBeds != null && beds !== f.exactBeds) return false;
        if (f.minBeds != null && beds < f.minBeds) return false;
        if (f.maxBeds != null && beds > f.maxBeds) return false;

        const baths = numeric(p.baths);
        if (f.exactBaths != null && baths !== f.exactBaths) return false;
        if (f.minBaths != null && baths < f.minBaths) return false;
        if (f.maxBaths != null && baths > f.maxBaths) return false;

        const cars = numeric(p.parkings);
        if (f.exactCarSpaces != null && cars !== f.exactCarSpaces) return false;
        if (f.minCarSpaces != null && cars < f.minCarSpaces) return false;

        if (f.minPrice != null || f.maxPrice != null) {
            const price = numeric(p.price);
            if (price > 0) {
                if (f.minPrice != null && price < f.minPrice) return false;
                if (f.maxPrice != null && price > f.maxPrice) return false;
            }
        }

        if (f.suburb) {
            const haystack = [p.suburb, p.address, p.title]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            if (!haystack.includes(f.suburb.toLowerCase())) return false;
        }

        if (f.category) {
            const type = String(p.property_type || p.category || "").toLowerCase();
            if (type && !type.includes(f.category)) return false;
        }

        if (f.methodOfSale) {
            const method = String(p.method_of_sale || "").toUpperCase().replace(/\s+/g, "_");
            if (method && method !== f.methodOfSale) return false;
        }

        if (f.features && f.features.length) {
            const blob = [
                p.title, p.headline, p.description,
                ...(p.feature_tags || []),
            ].filter(Boolean).join(" ").toLowerCase();
            for (const feat of f.features) {
                if (!blob.includes(feat.toLowerCase())) return false;
            }
        }

        if (kw) {
            const blob = [
                p.address, p.title, p.headline, p.description, p.suburb,
            ].filter(Boolean).join(" ").toLowerCase();
            const parts = kw.split(/\s+/).filter(Boolean);
            for (const part of parts) {
                if (!blob.includes(part)) return false;
            }
        }

        return true;
    });
};
